import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import { D3TooltipFollowerSkill } from 'tooltips';
import { SkillHeader, SkillActive, SkillInfo, SkillParams } from './skillInfo';
import { getStats } from 'logic/stats';
import { getFollowerBonus } from 'logic/skills';
import { getIconPath } from 'common';

const _L = Locale("ui-skills.js");

const FollowerSkillIcon = ({ active, info }) => (
  <span className="d3p-skill-icon d3p-follower-skill-icon" style={{backgroundImage: `url(${getIconPath(active ? info.icon : info.icon_inactive)})`}}>
    <span className="d3p-skill-frame"/>
  </span>
);

const FollowerActiveBox = React.memo(function FollowerActiveBox({ id, info }) {
  const state = Store.useState();
  const stats = getStats(state).follower;

  if (info.activeshow) {
    if (!info.activeshow(stats)) {
      return null;
    }
  } else {
    if (!getFollowerBonus(id, stats)) {
      return null;
    }
  }

  return <SkillActive info={info}/>;
});

const FollowerSkillCard = React.memo(function FollowerSkillCard({ id, active, onSelect }) {
  const extraRef = React.useRef();
  const onClick = React.useCallback(e => {
    if (extraRef.current?.contains(e.target)) {
      return;
    }
    onSelect(active ? null : id);
  }, [onSelect, active, id]);
  const info = Data.followerSkills[id];

  return (
    <div className={classNames("d3p-SkillCard d3p-follower", {"d3p-disabled": !active})}>
      <D3TooltipFollowerSkill id={id}>
        <SkillHeader icon={<FollowerSkillIcon info={info} active={active}/>}
                     name={info.name}
                     onClick={onClick}
        >
          {!!active && (<div className="d3p-passive-extra" ref={extraRef}>
            {!!(info && info.setActive) && <FollowerActiveBox id={id} info={info}/>}
            {!!(info && info.params) && <SkillParams info={info}/>}
            {!!(info && info.info) && <SkillInfo type="follower" id={id}/>}
          </div>)}
        </SkillHeader>
      </D3TooltipFollowerSkill>
    </div>
  );
});


function FollowerSkillGroup({ index }) {
  const allSkills = Store.useProfile(profile => {
    const item = profile.followerItems?.follower;
    const info = item && Data.itemById[item.id];
    return info?.required?.custom?.id === "leg_followerallskills";
  });
  const follower = Store.useProfile(profile => profile.follower);
  const selected = Store.useProfile(profile => profile.followerSkills?.[index]);

  const skill1 = Data.followerSkillList[follower]?.[index * 2];
  const skill2 = Data.followerSkillList[follower]?.[index * 2 + 1];

  const dispatch = Store.useDispatch();
  const onSelect = React.useCallback(skill => {
    if (allSkills) return;
    dispatch.profile.setFollowerSkill(index, skill);
  }, [dispatch, index, allSkills]);

  return (
    <div className="d3p-FollowerSkillGroup">
      <FollowerSkillCard id={skill1} active={allSkills || skill1 === selected} onSelect={onSelect}/>
      <FollowerSkillCard id={skill2} active={allSkills || skill2 === selected} onSelect={onSelect}/>
    </div>
  );
}

export default function FollowerSkillsSection() {
  const follower = Store.useProfile(profile => profile.follower);
  if (!follower) {
    return <div className="d3p-FollowerSkillGroup">{_L`No follower selected.`}</div>;
  }
  return (
    <React.Fragment>
      <FollowerSkillGroup index={0}/>
      <FollowerSkillGroup index={1}/>
      <FollowerSkillGroup index={2}/>
      <FollowerSkillGroup index={3}/>
    </React.Fragment>
  );
}
